import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Select } from '@mantine/core';

import { getHourMeterSummary } from '../../../../../slices/thunks';
import OChartCard from '../../../../../Components/OElements/OChartCard';
import OBarChart from '../../../../../Components/OElements/OBarChart';

const HOUR_SUMMARY_KEY = 'hour_summary_management';

const HourMeterManagementEChart = ({
	title,
	defaultFilters = {
		option: 'semana',
	},
	minHeight,
	maxHeight,
	companyId = null,
}) => {
	const dispatch = useDispatch();
	const [selectedDate, setSelectedDate] = useState(defaultFilters.option);

	const infoHourSummary = useSelector(
		state => state.FuelSupply.hourmeterSummary?.[HOUR_SUMMARY_KEY] || null
	);

	const fetchHourSummary = useCallback(
		(filter, type, company_id) => {
			dispatch(
				getHourMeterSummary({
					// NO establishment_id here - this is for management view
					filter: filter,
					type: type,
					group: 'establecimiento', // Group by establishment
					company_id: company_id, // Add company filter
				})
			);
		},
		[dispatch]
	);

	useEffect(() => {
		fetchHourSummary(selectedDate, HOUR_SUMMARY_KEY, companyId);
	}, [selectedDate, companyId, fetchHourSummary]);

	const { series, categories, legendData } = useMemo(() => {
		if (!infoHourSummary?.data || !Array.isArray(infoHourSummary.data))
			return { series: [], categories: [], legendData: [] };

		// Extract unique dates
		const dates = [...new Set(infoHourSummary.data.map(item => item.date))];

		// Extract unique establishments
		const establishmentsMap = new Map();
		infoHourSummary.data.forEach(item => {
			if (item.establishments && Array.isArray(item.establishments)) {
				item.establishments.forEach(est => {
					if (!establishmentsMap.has(est.establishment_id)) {
						establishmentsMap.set(
							est.establishment_id,
							est.establishment_name
						);
					}
				});
			}
		});

		const establishments = Array.from(establishmentsMap.entries());

		// Create series for each establishment
		const seriesData = establishments.map(([estId, estName]) => ({
			name: estName,
			type: 'bar',
			data: dates.map(date => {
				const dateData = infoHourSummary.data.find(
					item => item.date === date
				);
				if (!dateData || !dateData.establishments) return 0;

				const estData = dateData.establishments.find(
					e => e.establishment_id === estId
				);
				return estData ? Number(estData.total_hours) || 0 : 0;
			}),
		}));

		return {
			series: seriesData,
			categories: dates,
			legendData: establishments.map(([_, name]) => name),
		};
	}, [infoHourSummary?.data]);

	const options = useMemo(
		() => ({
			tooltip: {
				trigger: 'axis',
				axisPointer: { type: 'shadow' },
				formatter: function (params) {
					if (!params || !params.length) return '';
					let result = params[0].axisValue + '<br/>';
					let total = 0;

					params.forEach(param => {
						if (param.value > 0) {
							result +=
								param.marker +
								' ' +
								param.seriesName +
								': ' +
								param.value.toFixed(2) +
								' h<br/>';
							total += param.value;
						}
					});

					result += '<b>Total: ' + total.toFixed(2) + ' h</b>';
					return result;
				},
			},
			legend: {
				data: legendData,
				orient: 'horizontal',
				bottom: 10,
				type: 'scroll',
			},
			xAxis: [{ type: 'category', data: categories }],
			yAxis: [{ type: 'value', name: 'Horas' }],
			series: series,
		}),
		[series, categories, legendData]
	);

	const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
	const [hasValidData, setHasValidData] = useState(false);

	useEffect(() => {
		if (Array.isArray(infoHourSummary?.data)) {
			setHasLoadedOnce(true);
			if (infoHourSummary.data.length > 0) {
				setHasValidData(true);
			}
		}
	}, [infoHourSummary?.data]);

	// Only show empty state if we've loaded and confirmed there's no data
	const noData = hasLoadedOnce && !infoHourSummary?.data?.length;

	const actions = (
		<div className='btn-box-dashboard' style={{ display: 'flex', gap: 8 }}>
			<Select
				placeholder='Fecha'
				data={[
					{ label: 'Ciclo', value: 'ciclo' },
					{ label: 'Año', value: 'año' },
					{ label: 'Semestre', value: 'semestre' },
					{ label: 'Semana', value: 'semana' },
				]}
				value={selectedDate}
				onChange={value => value && setSelectedDate(value)}
				size='xs'
				radius='lg'
				styles={{ input: { width: 100 } }}
			/>
		</div>
	);

	return (
		<OChartCard
			title={title}
			actions={actions}
			isLoading={infoHourSummary?.isLoading && !hasLoadedOnce}
			error={infoHourSummary?.error}
			isEmpty={noData}
			emptyIconClass='ri-time-line'
			emptyMessage='No cuenta con registro de horas de generación'
		>
			<OBarChart
				options={options}
				minHeight={minHeight}
				maxHeight={maxHeight}
			/>
		</OChartCard>
	);
};

export default React.memo(HourMeterManagementEChart);
